import { FC, ReactElement } from 'react';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faUserGroup } from '@fortawesome/pro-solid-svg-icons';
import BoxIcon from 'assets/icons/icon-box.svg';
import { AccessLevel } from 'models/AccessLevel';
import type { Box } from 'models/Box';
import { contrastingForeground } from 'utils/ColorUtils';

type TileSize = 'sm' | 'md' | 'lg';

interface Props {
  box: Pick<Box, 'accessLevel' | 'display'>;
  // `md` (38px, default) for list rows; `sm` (32px) for the compact bar;
  // `lg` (52px) for the box header.
  size?: TileSize;
  // Lifts the untinted icon to primary ink, as on a selected row.
  selected?: boolean;
  className?: string;
}

// The box tile used in lists: the box's emoji, or the box icon on its
// colour, with a small badge on the corner saying whether the box is private
// (lock) or shared (two people).
const BoxTile: FC<Props> = ({
  box,
  size = 'md',
  selected = false,
  className,
}): ReactElement => {
  const tileColor = box.display?.tile.color ?? null;
  const tileEmoji = box.display?.tile.emoji ?? null;
  const isShared = box.accessLevel === AccessLevel.Shared;
  const s = styles[size];

  return (
    <div
      className={classNames(styles.container, s.container, className)}
      aria-hidden="true"
    >
      {/* Opaque card first so a coloured tile never lets the row show
          through; the custom colour paints over it. */}
      <div
        className={classNames(styles.face, s.face)}
        style={tileColor ? { backgroundColor: tileColor } : undefined}
      >
        {tileEmoji ? (
          <span className={classNames(styles.emoji, s.emoji)}>{tileEmoji}</span>
        ) : (
          <BoxIcon
            className={classNames(styles.icon(selected), s.icon)}
            style={
              tileColor
                ? { color: contrastingForeground(tileColor) }
                : undefined
            }
          />
        )}
      </div>
      {/* Private / shared at a glance. Always neutral: it states a fact
          about the box, which doesn't change with selection. */}
      <span className={classNames(styles.badge, s.badge)}>
        <FontAwesomeIcon
          icon={isShared ? faUserGroup : faLock}
          className={isShared ? s.badgeShared : s.badgeLock}
        />
      </span>
    </div>
  );
};

export default BoxTile;

const styles = {
  container: 'relative flex-none',
  face: classNames(
    'flex h-full w-full items-center justify-center',
    'border border-ink-secondary/25 bg-surface-card',
  ),
  // Untinted tiles: grey icon, lifting to primary ink when selected.
  icon: (selected: boolean) =>
    selected ? 'text-ink-primary' : 'text-ink-secondary',
  emoji: 'leading-none',
  badge: classNames(
    'absolute flex items-center justify-center rounded-full',
    'border border-ink-secondary/35 bg-surface-panel text-ink-secondary',
  ),
  // The two-person glyph is wide, so it needs a larger size than the lock
  // to read at the same weight.
  md: {
    container: 'w-[38px] h-[38px]',
    face: 'rounded-[10px]',
    icon: 'w-[18px] h-[18px]',
    emoji: 'text-lg',
    badge: '-top-[5px] -right-[5px] h-4 w-4',
    badgeLock: 'text-[7px]',
    badgeShared: 'text-[8.5px]',
  },
  lg: {
    container: 'size-[52px]',
    face: 'rounded-xl',
    icon: 'size-6',
    emoji: 'text-[28px]',
    badge: '-top-1.5 -right-1.5 size-5',
    badgeLock: 'text-[9px]',
    badgeShared: 'text-[10px]',
  },
  sm: {
    container: 'size-8',
    face: 'rounded-lg',
    icon: 'size-4',
    emoji: 'text-base',
    badge: '-top-1 -right-1 h-3.5 w-3.5',
    badgeLock: 'text-[6px]',
    badgeShared: 'text-[7.5px]',
  },
};
